import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck, faFilePdf, faDownload, faShieldHalved, faFingerprint,
} from '@fortawesome/free-solid-svg-icons';
import { generateSampleReportPDF } from '../../../../utils/assetReportPDF';
import './acts.css';
import './Act4_Reports.css';

export default function Act4Reports({ active }) {
  const [phase, setPhase] = useState(0); // 0=cover, 1=summary, 2=detail, 3=footer

  useEffect(() => {
    if (!active) { setPhase(0); return; }
    const timers = [];
    timers.push(setTimeout(() => setPhase(1), 600));
    timers.push(setTimeout(() => setPhase(2), 1200));
    timers.push(setTimeout(() => setPhase(3), 1800));
    return () => timers.forEach(clearTimeout);
  }, [active]);

  const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  const reportId = '#CS-2026-04-UCL44-0098';

  return (
    <div className="act act-4">
      {/* ── Narrative ── */}
      <div className="act-copy">
        <span className="act-eyebrow">
          <span className="act-eyebrow-dot" /> Act 4 · Reports
        </span>
        <h1 className="act-title">
          Proof the <span className="act-title-accent">auditor</span> wants.
        </h1>
        <p className="act-lede">
          One click generates a branded compliance report — cover page, executive summary,
          asset-by-asset test history, and a verifiable report ID. Ready for insurers,
          landlords, and HSE alike.
        </p>
        <ul className="act-proofs">
          <li className="act-proof">
            <span className="act-proof-check"><FontAwesomeIcon icon={faCheck} /></span>
            <span><strong>Audit-ready formatting</strong> — no hand-assembled PDFs or copy-pasting data.</span>
          </li>
          <li className="act-proof">
            <span className="act-proof-check"><FontAwesomeIcon icon={faCheck} /></span>
            <span><strong>Unique report ID</strong> on every export, logged in the audit trail.</span>
          </li>
          <li className="act-proof">
            <span className="act-proof-check"><FontAwesomeIcon icon={faCheck} /></span>
            <span><strong>Portfolio-wide or per-property</strong> — same format, same one click.</span>
          </li>
        </ul>
        <button
          className="act-cta"
          type="button"
          onClick={() => { try { generateSampleReportPDF(); } catch (e) { console.error('Sample PDF error:', e); } }}
        >
          <FontAwesomeIcon icon={faDownload} />
          <span>Download sample PDF</span>
        </button>
      </div>

      {/* ── Stage ── */}
      <div className="act-stage">
        <div className="a4-stack">
          {/* Behind pages */}
          <div className="a4-page a4-page-back a4-page-back-2" aria-hidden />
          <div className="a4-page a4-page-back a4-page-back-1" aria-hidden />

          {/* Front page */}
          <div className="a4-page a4-page-front">
            {/* Header band */}
            <div className="a4-cover">
              <div className="a4-cover-brand">
                <div className="a4-cover-mark">
                  <FontAwesomeIcon icon={faShieldHalved} />
                </div>
                <div>
                  <div className="a4-cover-brand-name">CloudSafe</div>
                  <div className="a4-cover-brand-sub">Compliance Report</div>
                </div>
              </div>
              <div className={`a4-cover-title ${phase >= 0 ? 'is-in' : ''}`}>
                <div className="a4-cover-meta">
                  <span>Prepared for</span>
                  <span className="a4-cover-meta-val">UCL East</span>
                </div>
                <div className="a4-cover-meta">
                  <span>Generated</span>
                  <span className="a4-cover-meta-val">{today}</span>
                </div>
                <div className="a4-cover-meta">
                  <span>Report ID</span>
                  <span className="a4-cover-meta-val a4-cover-id">{reportId}</span>
                </div>
              </div>
            </div>

            <div className="a4-body">
              {/* Exec summary */}
              <section className={`a4-section ${phase >= 1 ? 'is-in' : ''}`}>
                <h3 className="a4-section-title">Executive summary</h3>
                <div className="a4-summary">
                  <div className="a4-summary-cell">
                    <div className="a4-summary-val">198</div>
                    <div className="a4-summary-lbl">Assets</div>
                  </div>
                  <div className="a4-summary-cell a4-summary-cell-teal">
                    <div className="a4-summary-val">86</div>
                    <div className="a4-summary-lbl">Compliant</div>
                  </div>
                  <div className="a4-summary-cell a4-summary-cell-amber">
                    <div className="a4-summary-val">112</div>
                    <div className="a4-summary-lbl">Needs action</div>
                  </div>
                  <div className="a4-summary-cell a4-summary-cell-red">
                    <div className="a4-summary-val">0</div>
                    <div className="a4-summary-lbl">Failed</div>
                  </div>
                </div>
                <div className="a4-bar">
                  <div className="a4-bar-seg a4-bar-teal" style={{ width: '43%' }} />
                  <div className="a4-bar-seg a4-bar-amber" style={{ width: '57%' }} />
                </div>
                <div className="a4-bar-legend">
                  <span><span className="a4-legend-dot a4-legend-teal" /> Compliant 43%</span>
                  <span><span className="a4-legend-dot a4-legend-amber" /> Untested 57%</span>
                  <span><span className="a4-legend-dot a4-legend-red" /> Failed 0%</span>
                </div>
              </section>

              {/* Test history table */}
              <section className={`a4-section ${phase >= 2 ? 'is-in' : ''}`}>
                <h3 className="a4-section-title">Recent test activity</h3>
                <table className="a4-table">
                  <thead>
                    <tr>
                      <th>Asset</th>
                      <th>Location</th>
                      <th>Last test</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><code>UCL44-AS-001</code></td>
                      <td>Roof · Level 4</td>
                      <td>23 Apr 2026</td>
                      <td><span className="a4-pill a4-pill-teal">Pass · 6.2 kN</span></td>
                    </tr>
                    <tr>
                      <td><code>UCL44-AS-002</code></td>
                      <td>Roof · Level 4</td>
                      <td>23 Apr 2026</td>
                      <td><span className="a4-pill a4-pill-teal">Pass · 6.1 kN</span></td>
                    </tr>
                    <tr>
                      <td><code>UCL44-AS-003</code></td>
                      <td>Roof · Level 4</td>
                      <td>23 Apr 2026</td>
                      <td><span className="a4-pill a4-pill-teal">Pass · 6.0 kN</span></td>
                    </tr>
                    <tr>
                      <td><code>UCL44-LD-014</code></td>
                      <td>Mezz · A</td>
                      <td>14 Apr 2026</td>
                      <td><span className="a4-pill a4-pill-teal">Pass</span></td>
                    </tr>
                    <tr>
                      <td><code>UCL44-HR-008</code></td>
                      <td>Roof · Level 7</td>
                      <td>09 Apr 2026</td>
                      <td><span className="a4-pill a4-pill-teal">Pass</span></td>
                    </tr>
                  </tbody>
                </table>
              </section>

              {/* Footer */}
              <footer className={`a4-footer ${phase >= 3 ? 'is-in' : ''}`}>
                <div className="a4-footer-sign">
                  <FontAwesomeIcon icon={faFingerprint} />
                  <div>
                    <div className="a4-footer-sign-label">Verified export</div>
                    <div className="a4-footer-sign-val">{reportId} · logged to audit trail</div>
                  </div>
                </div>
                <div className="a4-footer-page">Page 1 of 14</div>
              </footer>
            </div>

            {/* Corner PDF tag */}
            <div className="a4-type">
              <FontAwesomeIcon icon={faFilePdf} />
              <span>PDF</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
